import { isAxiosError } from 'axios';

export const parsePaginationResponse = ({ data: { count, next, previous, results, ...rest } }) => ({
  count,
  next,
  previous,
  results,
  ...rest,
});

const FALLBACK_ERROR_MESSAGE = 'Erro ao processar requisição';

export function AppError(error) {
  const { message, fieldErrors, status } = parseResponseError(error);
  this.message = message;
  this.fieldErrors = fieldErrors;
  this.status = status;
}

AppError.prototype = Error.prototype;

/**
 * Parses API error responses into a standardized format
 * @param {Object} error - The error object from axios/API response
 * @param {Object} [error.response] - The response object containing status and data
 * @param {number} [error.response.status] - HTTP status code
 * @param {Object} [error.response.data] - Response data containing error information
 * @param {string} [defaultMessage='Erro ao processar requisição'] - Default error message if none is provided
 * @returns {Object} Standardized error object
 * @returns {string} return.message - General error message
 * @returns {Array<{field: string, errors: string[]}>} return.fieldErrors - Array of field-specific errors
 * @example
 * // Field validation errors
 * // Input:
 * // {
 * //   response: {
 * //     status: 400,
 * //     data: {
 * //       code: ["Cupom com este código já existe."],
 * //       anotherField: ["Another error"]
 * //     }
 * //   }
 * // }
 * // Output:
 * // {
 * //   message: "Erro ao processar requisição",
 * //   fieldErrors: [
 * //     { field: "code", errors: ["Cupom com este código já existe."] },
 * //     { field: "anotherField", errors: ["Another error"] }
 * //   ]
 * // }
 */
export const parseResponseError = (error) => {
  if (!isAxiosError(error)) {
    return {
      message: error.message || FALLBACK_ERROR_MESSAGE,
      fieldErrors: Array.isArray(error?.fieldErrors) ? error?.fieldErrors : [],
      status: error.status,
    };
  }

  const { status, data } = error.response ?? {
    status: 500,
  };

  const messages = {
    400: 'Erro ao processar requisição',
    401: 'Você não tem permissão para acessar este recurso',
    403: 'Você não tem permissão para acessar este recurso',
    404: 'Recurso não encontrado',
    500: 'Erro interno do servidor',
  };

  if (!status) {
    return {
      message: 'Erro de conexão, tente novamente em alguns minutos.',
      fieldErrors: [],
      status,
    };
  }

  if (status >= 400 && status < 500 && Array.isArray(data)) {
    const [message] = data || [messages[status] || FALLBACK_ERROR_MESSAGE];
    return {
      message,
      fieldErrors: [],
      status,
    };
  }

  if (status >= 400 && status < 500 && typeof data === 'object') {
    const { message, detail, ...rest } = data;

    const fieldErrors = Object.entries(rest).map(([field, errors]) => ({
      field,
      errors: Array.isArray(errors) ? errors : [errors],
    }));

    return {
      message: message || detail || messages[status] || FALLBACK_ERROR_MESSAGE,
      fieldErrors,
      status,
    };
  }

  if (status >= 500 && status < 600) {
    return {
      message: messages[500],
      fieldErrors: [],
      status,
    };
  }

  return {
    message: messages[status] || FALLBACK_ERROR_MESSAGE,
    fieldErrors: [],
    status,
  };
};
